import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import { doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import MessageModal from './MessageModal';
import ToggleSwitch from './ToggleSwitch';

const SellingStatus = ({ percentage }) => {
    let status = 'N/A', color = '#6c757d';
    if (percentage === null || isNaN(percentage)) {} 
    else if (percentage >= 80) { status = 'Profit'; color = 'var(--brand-success)'; }
    else if (percentage >= 50) { status = 'Average'; color = 'var(--brand-warning)'; }
    else if (percentage >= 30) { status = 'Poor'; color = '#fd7e14'; }
    else if (percentage >= 1) { status = 'Bad'; color = 'var(--brand-danger)'; }
    else { status = 'Loss'; color = '#343a40'; }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ height: '15px', width: '15px', backgroundColor: color, borderRadius: '50%', border: '1px solid var(--border-primary)' }}></span>
        <span style={{ fontSize: '1rem', fontWeight: '600' }}>{status}</span>
      </div>
    );
};

const AddItemForm = ({ user, parentProduct, newChildId, setView }) => {
    const [itemName, setItemName] = useState(parentProduct.itemName);
    const [itemDescription, setItemDescription] = useState(parentProduct.itemDescription);
    const [size, setSize] = useState('');
    const [color, setColor] = useState('');
    const [sellingPriceMYR, setSellingPriceMYR] = useState(parentProduct.sellingPriceMYR || 0);
    const [perUnitCostMYR, setPerUnitCostMYR] = useState(parentProduct.perUnitCostMYR || 0);
    const [isCostEditable, setIsCostEditable] = useState(false);
    const [profitMYR, setProfitMYR] = useState(0);
    const [profitPercentage, setProfitPercentage] = useState(0);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalState, setModalState] = useState({ message: '', onConfirm: null, showCancel: false });

    useEffect(() => {
        if (!imageFile) {
            setImagePreview('');
            return;
        }
        const objectUrl = URL.createObjectURL(imageFile);
        setImagePreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [imageFile]);

    useEffect(() => {
        const cost = parseFloat(perUnitCostMYR) || 0;
        const price = parseFloat(sellingPriceMYR) || 0;
        const newProfit = price - cost;
        setProfitMYR(newProfit);
        setProfitPercentage(cost > 0 ? (newProfit / cost) * 100 : 0);
    }, [sellingPriceMYR, perUnitCostMYR]);

    const handleImageChange = (e) => {
        if (e.target.files[0]) { setImageFile(e.target.files[0]); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setModalState({ message: 'Saving new item...', showCancel: false });

        let uploadedImageUrl = parentProduct.imageUrl || '';
        if (imageFile) {
            const storageRef = ref(storage, `product-images/${Date.now()}-${imageFile.name}`);
            const snapshot = await uploadBytes(storageRef, imageFile);
            uploadedImageUrl = await getDownloadURL(snapshot.ref);
        }

        const newItemRef = doc(db, "inventoryItems", newChildId);
        
        try {
            const newItemData = {
                parentDocId: parentProduct.id,
                parentReadableId: parentProduct.productId,
                childId: newChildId,
                status: 'Available',
                userId: user.uid,
                category: parentProduct.category,
                itemName,
                itemDescription,
                size,
                color,
                imageUrl: uploadedImageUrl,
                sellingPriceMYR: parseFloat(sellingPriceMYR) || 0,
                perUnitCostMYR: parseFloat(perUnitCostMYR) || 0,
                profitMYR: profitMYR,
                profitPercentage: profitPercentage,
                createdAt: new Date(),
                lastUpdatedAt: new Date(),
                editedBy: user.displayName,
            };
            
            await setDoc(newItemRef, newItemData);
            setModalState({ message: `Successfully added new item: ${newChildId}`, onConfirm: () => setView('dashboard'), showCancel: false });

        } catch (err) {
            console.error("Error adding new item:", err);
            setModalState({ message: `Failed to add item: ${err.message}`, onConfirm: () => setModalState({ message: ''}), showCancel: false });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <MessageModal {...modalState} />
            <form onSubmit={handleSubmit} className="product-form-container">
                <div className="product-form-main">
                    <div className="form-section">
                        <h3>Add New Item to Batch: {parentProduct.productId}</h3>
                        <h4 style={{color: 'var(--brand-primary)'}}>New Item ID: {newChildId}</h4>
                        <div className="form-grid">
                            <div className="form-field"><label>Item Name</label><input type="text" value={itemName} onChange={(e) => setItemName(e.target.value)} required /></div>
                            <div className="form-field"><label>Category</label><input type="text" value={parentProduct.category} readOnly /></div>
                            <div className="form-field full-span"><label>Item Description</label><textarea value={itemDescription} onChange={(e) => setItemDescription(e.target.value)} rows="3"></textarea></div>
                            <div className="form-field"><label>Size</label><input type="text" value={size} onChange={e => setSize(e.target.value)} placeholder="e.g., Medium, 12-inch" /></div>
                            <div className="form-field"><label>Color</label><input type="text" value={color} onChange={e => setColor(e.target.value)} placeholder="e.g., Gold, Royal Blue" /></div>
                        </div>
                    </div>
                    <div className="form-section profit-analysis-section">
                        <h3>Financials</h3>
                        <div className="form-grid">
                            <div className="form-field">
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
                                    <label style={{marginBottom: 0}}>Per Unit Cost (RM)</label>
                                    <ToggleSwitch isEnabled={isCostEditable} onToggle={() => setIsCostEditable(!isCostEditable)} label="Edit Cost" />
                                </div>
                                <input type="number" step="0.01" value={perUnitCostMYR} onChange={e => setPerUnitCostMYR(e.target.value)} readOnly={!isCostEditable} required />
                            </div>
                            <div className="form-field"><label>Selling Price (RM)</label><input type="number" step="0.01" value={sellingPriceMYR} onChange={e => setSellingPriceMYR(e.target.value)} required /></div>
                            <div className="form-field"><label>Planned Profit (RM)</label><input type="text" value={profitMYR.toFixed(2)} readOnly style={{color: profitMYR < 0 ? 'var(--brand-danger)' : 'var(--brand-success)', fontWeight: 'bold'}} /></div>
                            <div className="form-field"><label>Planned Selling Status</label><SellingStatus percentage={profitPercentage} /></div>
                        </div>
                    </div>
                </div>
                <div className="product-form-sidebar">
                    <div className="form-section">
                        <h3>Product Image</h3>
                        <div className="form-field">
                            <label>Upload Image</label>
                            <input type="file" onChange={handleImageChange} accept="image/*" />
                        </div>
                        {imagePreview && (
                            <div className="form-field">
                                <label>Image Preview</label>
                                <div className="image-preview-box"><img src={imagePreview} alt="Preview"/></div>
                            </div>
                        )}
                    </div>
                    <div className="form-actions" style={{flexDirection: 'column', gap: '1rem'}}>
                        <button type="submit" className="submit-button" style={{width: '100%'}} disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Add Item'}</button>
                        <button type="button" className="cancel-button" style={{width: '100%', margin: 0}} onClick={() => setView('dashboard')} disabled={isSubmitting}>Cancel</button>
                    </div>
                </div>
            </form>
        </>
    );
};

export default AddItemForm;